import { z } from "zod";
import {
    AccountMergeStatus,
    PreviewAccountChange,
    PreviewAccountOrigin,
} from "../types/domains/accounting/chartOfAccountsTemplateApply";
import { AuditLogFilterObjectSchema } from "../contracts/auditLog.contract";

export const ChartOfAccountsTemplateApplyBodySchema = z.object({
    applyInvocationId: z.string().uuid().optional(),
    entityIds: z
        .array(z.number().int().positive())
        .min(1)
        .max(50)
        .refine((ids) => new Set(ids).size === ids.length, {
            message: "Entity IDs must be unique",
        }),
});

const ChartOfAccountsTemplateActivityFilterSchema =
    AuditLogFilterObjectSchema.extend({
        actorId: z.coerce.number().int().positive().optional(),
    }).refine((data) => !data.showReverted || !data.revertedOnly, {
        message: "showReverted and revertedOnly are mutually exclusive",
    });

export const ChartOfAccountsTemplateActivityQuerySchema = z
    .object({
        filters: ChartOfAccountsTemplateActivityFilterSchema.optional(),
        page: z.coerce.number().int().min(1).default(1),
        limit: z.coerce.number().int().min(1).max(100).default(20),
    })
    .superRefine(({ filters }, context) => {
        if (
            filters?.minCreatedAt &&
            filters.maxCreatedAt &&
            filters.minCreatedAt > filters.maxCreatedAt
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["filters", "maxCreatedAt"],
                message: "maxCreatedAt must be on or after minCreatedAt",
            });
        }
    });

export type ChartOfAccountsTemplateApplyBody = z.infer<
    typeof ChartOfAccountsTemplateApplyBodySchema
>;

// A single account row in the preview. `id` is the real entity-account id for
// existing accounts, or a deterministic temp id ("new:<templateAccountId>") for
// accounts the apply would create.
export const PreviewAccountSchema = z.object({
    id: z.string(),
    code: z.number().int().nullable(),
    codeText: z.string().nullable(),
    name: z.string(),
    // The code the account "would have had" before the merge changed it: set
    // ONLY for a created account whose template code was bumped to the
    // first-available code due to a clash. `null` when there was no clash.
    previousCode: z.number().int().nullable(),
    // Kept as strings: an entity account's type/subtype/class span a v1|v2 enum
    // union (plus legacy values), so a stricter nativeEnum would reject valid
    // historical rows under `validateResponses`.
    type: z.string(),
    subtype: z.string().nullable(),
    class: z.string().nullable(),
    isDisabled: z.boolean(),
    isManuallyCreated: z.boolean(),
    parentGroupId: z.string().nullable(),
    mergeStatus: z.nativeEnum(AccountMergeStatus),
    // Final post-apply slot index within the account's (type, parent) scope, so
    // a preview consumer can render rows in the exact order an apply would
    // produce without re-deriving the merge engine's ordering pass. Groups carry
    // no order — their preview position is derived from their member accounts.
    sortOrder: z.number(),
    // Where this row sits in the template <> entity diff (drives the Origin
    // filter). `templateOnly` = added, `matched` = existed + matched a template
    // account, `entityOnly` = existed with no template match (untouched).
    origin: z.nativeEnum(PreviewAccountOrigin),
    // Material changes the apply would make (drives tags + the Updates filter).
    changes: z.array(z.nativeEnum(PreviewAccountChange)),
    // Breadcrumb of the group this account is moving FROM (e.g.
    // "Expenses › Contractors"), set only when `changes` includes `reassigned`.
    previousGroupPath: z.string().nullable(),
});

export type PreviewAccount = z.infer<typeof PreviewAccountSchema>;

export const PreviewGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    parentGroupId: z.string().nullable(),
    isNew: z.boolean(),
    origin: z.nativeEnum(PreviewAccountOrigin),
});

export type PreviewGroup = z.infer<typeof PreviewGroupSchema>;

export const ChartOfAccountsTemplatePreviewEntitySchema = z.object({
    entityId: z.number().int(),
    accounts: z.array(PreviewAccountSchema),
    groups: z.array(PreviewGroupSchema),
});

export type ChartOfAccountsTemplatePreviewEntity = z.infer<
    typeof ChartOfAccountsTemplatePreviewEntitySchema
>;

export const ChartOfAccountsTemplatePreviewResponseSchema = z.object({
    entities: z.array(ChartOfAccountsTemplatePreviewEntitySchema),
});

export type ChartOfAccountsTemplatePreviewResponse = z.infer<
    typeof ChartOfAccountsTemplatePreviewResponseSchema
>;

// Per-entity outcome of an apply: how many accounts the merge created and how
// many existing accounts it materially updated (unchanged matches excluded).
export const ChartOfAccountsTemplateApplyEntityResultSchema = z.object({
    entityId: z.number().int(),
    workspaceId: z.string().uuid(),
    applyEventId: z.string().uuid(),
    accountsCreated: z.number().int(),
    accountsUpdated: z.number().int(),
});

export type ChartOfAccountsTemplateApplyEntityResult = z.infer<
    typeof ChartOfAccountsTemplateApplyEntityResultSchema
>;

export const ChartOfAccountsTemplateApplyResponseSchema = z.object({
    applyInvocationId: z.string().uuid(),
    results: z.array(ChartOfAccountsTemplateApplyEntityResultSchema),
});

export type ChartOfAccountsTemplateApplyResponse = z.infer<
    typeof ChartOfAccountsTemplateApplyResponseSchema
>;

export const ChartOfAccountsTemplateAppliedEntitySchema = z.object({
    entityId: z.number().int().positive(),
    appliedAt: z.coerce.date(),
});

export type ChartOfAccountsTemplateAppliedEntity = z.infer<
    typeof ChartOfAccountsTemplateAppliedEntitySchema
>;

export const ChartOfAccountsTemplateAppliedEntitiesResponseSchema = z.object({
    appliedEntities: z.array(ChartOfAccountsTemplateAppliedEntitySchema),
});

export type ChartOfAccountsTemplateAppliedEntitiesResponse = z.infer<
    typeof ChartOfAccountsTemplateAppliedEntitiesResponseSchema
>;
